
import "./mail.scss";
import {
    Button,
    Tag,
    Card,
    Select,
    DatePicker,
    Space,
    Typography,
    Row,
    Col,
    Alert,
    Divider, Input
} from 'antd';
import {
    SendOutlined,
    DeleteOutlined,
    GiftOutlined,
    CalendarOutlined,
    InboxOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { PlayerContext } from "../../../../shared/contexts/playerContext"
import { internalApi } from "../../../../common/axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import TablePage from "../../../../pages/tablePage/TablePage";
import { AuthContext } from "../../../../shared/contexts/authContext";
import { DarkModeContext } from "../../../../shared/contexts/darkModeContext";
import { getItemName, getMailTitle } from "../../../../shared/utils/basic";
import { useItemKrTextData } from "../../../probability/useItemData";
import IsLoading from "../../../../shared/components/IsLoading";
import ErrorPage from "../../../../shared/components/ErrorPage";
import SearchDropDown from "../../../../shared/components/common/SearchDropDown";
import {useMailTitle} from "../../../country/useMailTitle";
import {MAIL_TYPE} from "../../../../shared/utils/enum";
import {getMailTypeColorAndText} from "../../../../shared/utils/typeChecks";

const { Text } = Typography;
const { Option } = Select;

const defaultMailData = {
    title: '',
    expired: 3
};

const Mail = () => {
    const { playerData } = useContext(PlayerContext);
    const { currentUser } = useContext(AuthContext);
    const { darkMode } = useContext(DarkModeContext);
    const [mail, setMail] = useState(defaultMailData);
    const [mailRowData, setMailRowData] = useState({});
    const [mailSendRowData, setMailSendRowData] = useState({});
    const [items, setItems] = useState([{ id: 101, type: 1, quantity: 0 }]);
    const [memo, setMemo] = useState('');
    const refreshRef = useRef(null);
    const navigate = useNavigate();

    const { data: mailTitle, isLoading: isMailTitleLoading, error: isMailTitleError } = useMailTitle();
    const { data: krItemTextData, isLoading, error } = useItemKrTextData();

    useEffect(() => {
        if (!playerData.userKey) {
            navigate('/player');
        }
    }, [playerData, navigate]);

    const handleTitleChange = (value) => {
        setMail(prevMail => ({
            ...prevMail,
            title: value
        }));
    };

    const handleDateChange = (date) => {
        if (date) {
            const expiredDays = date.endOf('day').diff(dayjs().startOf('day'), 'day');
            setMail(prevMail => ({
                ...prevMail,
                expired: expiredDays
            }));
        }
    };

    useEffect(() => {
        setMail(prevMail => ({
            ...prevMail,
            items
        }))
    }, [items]);

    const handleSendMail = () => {
        if (mail.title !== '') {
            const mailData = {
                userKey: playerData.userKey,
                mail,
                memo
            };

            internalApi.post('player/mail', mailData).then((res) => {
                toast.success(res.data.message);
                if (refreshRef.current) {
                    refreshRef.current();
                }
                setMail(defaultMailData);
                setItems([{ id: 101, type: 1, quantity: 0 }]);
            }).catch((err) => {
                console.error('Error sending mail:', err);
                toast.error('메일 발송 실패!');
            });
        } else {
            toast.error('제목을 입력해주세요');
        }
    }

    const handleDelete = (record) => {
        internalApi.delete(`player/mail/${record.msn}`).then((res) => {
            toast.success(res.data.message);
            if (refreshRef.current) {
                refreshRef.current();
            }
        }).catch((err) => {
            console.error('Error deleting mail:', err);
            toast.error('메일 삭제 실패!');
        });
    };

    const mailListColumns = useMemo(() => ([
        {
            title: '우편 인덱스',
            dataIndex: 'msn',
            key: 'msn',
            width: 100,
            fixed: 'left',
        },
        {
            title: 'Type',
            dataIndex: 'mailType',
            width: 100,
            render: (_, { mailType }) => {
                const {color, text} = getMailTypeColorAndText(mailType);
                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: 'Title',
            dataIndex: 'title',
            ellipsis: true,
            render: (_, { title }) => getMailTitle(mailTitle, krItemTextData[currentUser.target], title)
        },
        {
            title: '아이템 이름',
            dataIndex: 'itemId',
            width: 150,
            ellipsis: true,
            render: (_, { itemId, itemType, productId }) => getItemName(krItemTextData[currentUser.target], itemId, itemType, productId)
        },
        {
            title: '수량',
            dataIndex: 'quantity',
            width: 100,
            render: (quantity) => (
                <Text strong style={{ color: '#1890ff' }}>
                    {quantity?.toLocaleString()}
                </Text>
            )
        },
        {
            title: '확인 여부',
            dataIndex: 'isNew',
            width: 100,
            render: (_, { isNew }) => (
                <Tag color={isNew ? 'gold' : 'success'}>
                    {isNew ? '미확인' : '확인'}
                </Tag>
            ),
        },
        {
            title: '수신일',
            dataIndex: 'created',
            width: 160,
            render: (created) => (
                <Text>{dayjs(created).format('YYYY-MM-DD HH:mm')}</Text>
            )
        },
        {
            title: 'Expired',
            dataIndex: 'expired',
            width: 160,
            render: (expired) => (
                <Text type={dayjs(expired).isBefore(dayjs()) ? 'danger' : 'secondary'}>
                    {dayjs(expired).format('YYYY-MM-DD HH:mm')}
                </Text>
            )
        },
        {
            title: 'Action',
            dataIndex: '',
            key: 'action',
            render: (text, record) => (
                <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record)}
                >
                    Delete
                </Button>
            ),
            fixed: 'right',
            width: 100,
        }
    ]), [mailTitle, krItemTextData, currentUser.target]);
    const mailSendLogColumns = [
        {
            title: '발송인',
            dataIndex: 'adminName',
            key: 'adminName',
        },
        {
            title: '발송 아이템',
            dataIndex: 'rewards',
            key: 'rewards',
            render: (_, { rewards }) => (
                <div>
                    {
                        JSON.parse(rewards).map((reward, idx) => (
                            <div key={idx}>
                                <span>{getItemName(krItemTextData[currentUser.target], reward.id, reward.type)}({reward.id})</span>
                                <span> | 수량: {reward.quantity}</span>
                            </div>
                        ))
                    }
                </div>
            )

        },
        {
            title: '메모',
            dataIndex: 'memo',
            key: 'memo',
        },
        {
            title: '발송일',
            dataIndex: 'created',
            key: 'created',
        }
    ]

    const mailListProps = {
        columns: mailListColumns,
        refreshRef,
        url: `player/mail?userKey=${playerData.userKey}`,
        rowData: mailRowData,
        setRowData: setMailRowData
    }

    const mailSendLogProps = {
        columns: mailSendLogColumns,
        refreshRef,
        url: `player/mailSendLog?userKey=${playerData.userKey}`,
        rowData: mailSendRowData,
        setRowData: setMailSendRowData
    }

    if (isLoading || isMailTitleLoading) return <IsLoading />;
    const firstError = error || isMailTitleError;
    if (firstError) return <ErrorPage error={firstError} />;

    return (
        <div className={`mail-main ${darkMode ? 'dark-theme' : 'light-theme'}`}>
            <div style={{ padding: '24px' }}>
                <Row gutter={[0, 24]}>
                    {/* 새 메일 작성 */}
                    <Col span={24}>
                        <Card
                            className="new-mail-card"
                            title={
                                <Space align="center">
                                    <SendOutlined style={{ color: '#52c41a' }} />
                                    <span>새 메일 발송</span>
                                </Space>
                            }
                            style={{
                                borderRadius: '8px',
                                border: `1px solid var(--border-color)`,
                                background: `var(--card-background)`,
                                boxShadow: 'var(--card-shadow)'
                            }}
                            styles={{
                                header: {
                                    borderBottom: `1px solid var(--border-color)`,
                                    background: `var(--header-background)`
                                },
                                body: {
                                    padding: '24px'
                                }
                            }}
                        >
                            <Alert
                                message="메일 발송 안내"
                                description={`${playerData.playerInfo?.userInfo?.Nickname || playerData.userKey}님에게 아이템과 함께 메일을 발송할 수 있습니다.`}
                                type="info"
                                icon={<ExclamationCircleOutlined />}
                                style={{ marginBottom: '24px' }}
                            />

                            <Row gutter={[16, 16]}>
                                {/* 메일 제목 */}
                                <Col xs={24} md={12}>
                                    <div className="form-item">
                                        <Text strong style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                                            메일 제목
                                        </Text>
                                        <Select
                                            placeholder="메일 제목을 선택하세요"
                                            value={mail.title}
                                            onChange={handleTitleChange}
                                            size="large"
                                            style={{ width: '100%' }}
                                            className="mail-select"
                                            showSearch
                                            filterOption={(input, option) =>
                                                option.children.props.children[0].props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                            }
                                            optionLabelProp="label"
                                        >
                                            {mailTitle.map((option) => {
                                                const koTitle = option.title.find((t) => t.langCode === 'ko')?.text || 'Unknown';
                                                return (
                                                    <Option
                                                        key={option.index}
                                                        value={option.index}
                                                        label={koTitle}
                                                    >
                                                        <div>
                                                            <div style={{ fontWeight: 'bold' }}>
                                                                {koTitle}
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: '#999' }}>
                                                                ID: {option.index}
                                                            </div>
                                                        </div>
                                                    </Option>
                                                );
                                            })}
                                        </Select>
                                    </div>
                                </Col>

                                {/* 만료일 */}
                                <Col xs={24} md={6}>
                                    <div className="form-item">
                                        <Text strong style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                                            만료일
                                        </Text>
                                        <DatePicker
                                            value={dayjs().endOf('day').add(mail.expired, 'day')}
                                            onChange={handleDateChange}
                                            size="large"
                                            style={{ width: '100%' }}
                                            className="mail-datepicker"
                                            format="YYYY-MM-DD"
                                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                                            suffixIcon={<CalendarOutlined />}
                                        />
                                    </div>
                                </Col>
                                <Col xs={24} md={6}>
                                    <div className="form-item">
                                        <Text strong style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                                            메모
                                        </Text>
                                        <Input
                                            className="memo"
                                            value={memo}
                                            onChange={(value) => setMemo(value.target.value)}
                                            size="large"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </Col>

                                {/* 아이템 선택 - 전체 너비 */}
                                <Col span={24}>
                                    <div className="form-item">
                                        <Text strong style={{ display: 'block', marginBottom: '8px', color: 'var(--text-primary)' }}>
                                            <GiftOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                                            아이템 선택
                                        </Text>
                                        <div className="item-selection-container">
                                            <SearchDropDown
                                                dataArr={krItemTextData[currentUser.target]}
                                                items={items}
                                                setItems={setItems}
                                                hasQuantity={true}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <Divider style={{ margin: '24px 0', borderColor: 'var(--border-color)' }} />

                            {/* 발송 버튼 */}
                            <div style={{ textAlign: 'center' }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<SendOutlined />}
                                    onClick={handleSendMail}
                                    className="send-button"
                                    style={{
                                        height: '48px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '160px'
                                    }}
                                >
                                    메일 발송
                                </Button>
                            </div>
                        </Card>
                    </Col>

                    {/* 메일 목록 */}
                    <Col span={24}>
                        <Card
                            className="mail-list-card"
                            title={
                                <Space align="center">
                                    <InboxOutlined style={{ color: '#1890ff' }} />
                                    <span>메일 목록</span>
                                    <Tag color="blue" style={{ marginLeft: '8px' }}>
                                        {playerData.playerInfo?.userInfo?.Nickname || playerData.userKey}
                                    </Tag>
                                </Space>
                            }
                            style={{
                                borderRadius: '8px',
                                border: `1px solid var(--border-color)`,
                                background: `var(--card-background)`,
                                boxShadow: 'var(--card-shadow)'
                            }}
                            styles={{
                                header: {
                                    borderBottom: `1px solid var(--border-color)`,
                                    background: `var(--header-background)`
                                },
                                body: {
                                    padding: 0
                                }
                            }}
                        >
                            <TablePage {...mailListProps} />
                        </Card>
                    </Col>
                    <Col span={24}>
                        <Card
                            className="mail-list-card"
                            title={
                                <Space align="center">
                                    <InboxOutlined style={{ color: '#1890ff' }} />
                                    <span>운영 메일 발송 로그</span>
                                </Space>
                            }
                            style={{
                                borderRadius: '8px',
                                border: `1px solid var(--border-color)`,
                                background: `var(--card-background)`,
                                boxShadow: 'var(--card-shadow)'
                            }}
                            styles={{
                                header: {
                                    borderBottom: `1px solid var(--border-color)`,
                                    background: `var(--header-background)`
                                },
                                body: {
                                    padding: 0
                                }
                            }}
                        >
                            <TablePage {...mailSendLogProps} />
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Mail;